import { createContext, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRequest } from "../../Hooks/useRequest";
import VisualizationArea from "./VisualizationArea";
import loadingSvg from "./smartneuro.svg";
import { useEndpointProvider } from '../../Providers/EndpointProvider';
import * as MdIcons from "react-icons/md";
import { useAuthProvider } from "../../Providers/AuthProvider";
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider";
import { hotkeysAtom, loadHotkeysFromConfigs } from "../../Pages/Data/Visualize/DataReview/Atoms/Hotkeys"
import { useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { currentPatientFileInfoAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/PatientFile";
import { activeAnnotationSessionAtom, annotationGroupsAtom, annotationsAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { useOnMount } from "../../Hooks/useOnMount";
import { layoutGroupsAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/Layout";
import { pageManagerRegistry } from "../../Pages/Data/Visualize/DataReview/Data/PageManagerRegistry";
import { viewScaleRegistry } from "../../Pages/Data/Visualize/DataReview/Data/ViewScaleRegistry";
import { useModalProvider } from '../../Providers/ModalProvider';
import { MobergButton, MobergButtonShape } from "../../Components/MobergButton/MobergButton";
import { MobergIconSize } from "../../Components/MobergIcon/MobergIcon";
import { useAnnotationService } from "../../Hooks/useAnnotationService";
import { eegMontagesAtom } from "../../Pages/Data/Visualize/DataReview/Atoms/EEGMontage";
import { fileScaleRegistry } from "../../Pages/Data/Visualize/DataReview/Data/FileScaleRegistry";
import { useEEGMontageService } from "../../Hooks/useEEGMontageService";
import { DataSource } from "../../Pages/Data/Visualize/DataReview/Types/DataSource";
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider";
import { Deployment, useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider";
import { usePrefetchData } from "../../Pages/Data/Visualize/DataReview/Hooks/usePrefetchData"
import { useModalitiesProvider } from "../../Providers/ModalitiesProvider";
import { useSocketProvider } from "../../Providers/SocketProvider";
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks";

export const VisualizationContext = createContext(null)

const prefetchId = Math.floor(Math.random() * 10000)

export function Visualization(props) {
	// TODO: is all of this necessary in the URL? No. But it gets Data Review on its own page, which is more important right now.
	let { patientID, patientIdentifier, siteName, dataObjectID, dataObjectName } = useParams()
	
	patientID = parseInt(patientID)
	dataObjectID = parseInt(dataObjectID)

	const navigate = useNavigate()
	const { closeDataQuerySocketConnections } = useSocketProvider()
	const resetAnnotations = useResetRecoilState(annotationsAtom)
	const workspacesProvider = useWorkspacesProvider()
	const authProvider = useAuthProvider()
	const endpointProvider = useEndpointProvider()
	const { setAllPatientModalities } = useModalitiesProvider()
	const [, setPatientFileInfo] = useRecoilState(currentPatientFileInfoAtom)
	const setAnnotationGroups = useSetRecoilState(annotationGroupsAtom)
	const setActiveAnnotationSession = useSetRecoilState(activeAnnotationSessionAtom)
	const [layoutsLoaded, setLayoutLoaded] = useState(false)
	const setLayoutGroups = useSetRecoilState(layoutGroupsAtom)
	const [eegMontages, setEEGMontages] = useRecoilState(eegMontagesAtom)
	const [eegMontageMapping, setEEGMontageMapping] = useState()
	const [persystRecordingInfo, setPersystRecordingInfo] = useState()
	const [persystMontageDefinitions, setPersystMontageDefinitions] = useState()
	const [admissionInfo, setAdmissionInfo] = useState()
	const eegMontagesLoaded = eegMontages.length > 0
	const { createAnnotationGroup } = useAnnotationService()
	const { deserializeQueryMontage } = useEEGMontageService()
	const { LINKS } = useBackendLinksProvider()
	const { deployment } = useEnvironmentVariablesProvider()

	const isDevelopment = deployment === Deployment.DEVELOPMENT
	const prefetchDisabled = deployment === Deployment.CLINICAL
	const isDemo = workspacesProvider.selectedWorkspace === "DEMO"

	const [ready, setReady] = useState(false)

	const { close, createModal } = useModalProvider()
	const { prefetchMessage, startPrefetch, skipPrefetch, stopPrefetch, prefetchIsFinished, enoughSpace } = usePrefetchData({
		prefetchId,
		dataObjectId: dataObjectID,
		fileToPreload: props.selectedFile
	})


	function goBack() {
		stopPrefetch()
		handleCloseVisualization()
		close()
	}

	function handleCloseVisualization() {
		// Memory Cleanup
		pageManagerRegistry.clear()
		viewScaleRegistry.clear()
		fileScaleRegistry.clear()

		// Socket Cleanup
		closeDataQuerySocketConnections()
		navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.SUBPAGES.PATIENTS.MAIN}?patient_id=${patientID}`)
	}

	const [modalities, modalitiesLoaded, getModalitiesUnion] = useRequest(
		`${LINKS.DATA.TRIALS.GET_MODALITIES_UNION.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		() => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': 'application/json',
				'Authorization': authProvider.token
			},
			body: JSON.stringify({
				workspace_name: workspacesProvider.selectedWorkspace,
				patient_ids: [],
			})
		})
	)

	const [patientModalities, patientModalitiesLoaded, getModalitiesIntersection] = useRequest(
		`${LINKS.DATA.TRIALS.GET_MODALITIES_UNION.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		() => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': 'application/json',
				'Authorization': authProvider.token
			},
			body: JSON.stringify({
				workspace_name: workspacesProvider.selectedWorkspace,
				patient_ids: [patientID],
			})
		})
	)

	const [hotkeysLoaded, setHotkeysLoaded] = useState(false)
	const [hotkeys, setHotkeys] = useRecoilState(hotkeysAtom)

	useOnMount(() => {
		const body = {
			data_object_id: dataObjectID
		}

		endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATION_GROUPS, body)
			.then(annotationGroups => {
				setAnnotationGroups(annotationGroups)

				if (annotationGroups.length > 0) {
					setActiveAnnotationSession(annotationGroups[0])
				} else {
					createAnnotationGroup("Default Session", dataObjectID).then(() => {
						endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATION_GROUPS, body)
							.then(annotationGroups => {
								setAnnotationGroups(annotationGroups)
								if (annotationGroups.length > 0) {
									setActiveAnnotationSession(annotationGroups[0])
								}
							})
					})
				}
			})
			.catch(error => alert(error))
	})

	useEffect(() => {
		endpointProvider.post(LINKS.ACCOUNT.GET_KEYBOARD_SHORTCUTS, {})
			.then(hotkeyConfigs => {
				setHotkeys(loadHotkeysFromConfigs(hotkeyConfigs))
				setHotkeysLoaded(true)
			})
			.catch(e => console.error(e))
	}, [endpointProvider, setHotkeys])

	const updateHotkeyConfig = useCallback((newHotKeyConfigs, callback) => {
		const res = newHotKeyConfigs.map((config) => {
			const newConfig = { id: config.id, action: config.action, triggers: config.triggers }
			if (config.data) { newConfig.data = config.data }
			return newConfig
		})
		endpointProvider.post(LINKS.ACCOUNT.UPDATE_KEYBOARD_SHORTCUTS, { "configs": res }).then(
			(s) => { callback(s) }).catch(e => { console.error(e) })
	}, [endpointProvider])

	const resetHotkeyConfig = useCallback((callback) => {
		endpointProvider.post(LINKS.ACCOUNT.RESET_KEYBOARD_SHORTCUTS, {}).then(
			(s) => { callback(s) }).catch(e => { console.error(e) })
	}, [endpointProvider])

	const [startEndTimestamps, startEndTimestampsLoaded, getStartEndTimestamps] = useRequest(
		`${LINKS.DATA.PROFILING.GET_PATIENT_FILE_START_END_TIMESTAMPS.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		patient_id => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': "application/json",
				'Authorization': authProvider.token,
			},
			body: JSON.stringify({
				patient_ids: [patientID],
				workspace_name: workspacesProvider.selectedWorkspace
			})
		})
	)

	const getPersystRecordingInfo = () => endpointProvider.post(LINKS.VISUALIZE.PERSYST.GET_RECORDING_INFO, 
		{
			workspaceName: workspacesProvider.selectedWorkspace,
			data_object_id: dataObjectID
		})
		.then(recordingInfo => {
			setPersystRecordingInfo({
				recordingStartDate: new Date(recordingInfo["start_time_milliseconds"]),
				recordingEndDate: new Date(recordingInfo["end_time_milliseconds"])
			})
		})
		.catch(err => {
			// Persyst may not be configured for the workspace and that's okay.
			// This is a potential place where we could disable Persyst displays for Data Review.
			console.warn(err)
			setPersystRecordingInfo(null)
		})

	const [USER_INFO, userInfoLoaded, getUserInfo] = useRequest(
		`${LINKS.ACCOUNT.GET_CURRENT_USER.LINK}?parent_study_id=${workspacesProvider.selectedWorkspace}`,
		'JSON',
		() => ({
			method: "POST",
			cache: "no-cache",
			headers: {
				'content-type': "application/json",
				'Authorization': authProvider.token,
			},
			body: JSON.stringify({
				study_id: ''
			})
		})
	)

	const getLayouts = () => {
		endpointProvider.post(LINKS.DATA.PROFILING.GET_LAYOUTS)
			.then(layoutGroups => {
				setLayoutGroups(layoutGroups)
				setLayoutLoaded(true)
			})
			.catch(error => alert("Error with getting layouts: " + error))
	}

	const getEEGMontages = () => {
		endpointProvider.post(LINKS.VISUALIZE.EEG.GET_EEG_MONTAGES)
			.then(eegMontages => {
				setEEGMontages(eegMontages.map(deserializeQueryMontage))
			})
			.catch(error => alert("Error with getting EEG Montages: " + error))
	}

	const getPersystMontages = () => endpointProvider.get(LINKS.DATA.PROFILING.PERSYST.EEG_MONTAGES, { data_object_id: dataObjectID })
			.then(montageGroups => {
				const system = montageGroups.find(group => group.groupName === "System")
	
				if (!system) {
					return
				}
	
				const montages = system.definitions
				setPersystMontageDefinitions(montages)
			})
			.catch(err => {
				console.warn(err)
				setPersystMontageDefinitions([])
			})

	const getEEGMontageMapping = () => {
		endpointProvider.post(LINKS.VISUALIZE.EEG.GET_MONTAGE_MAPPING, { data_object_id: dataObjectID })
			.then(mapping => setEEGMontageMapping(new Map(Object.entries(mapping))))
			.catch(error => alert("Error with getting EEG Montage Mapping: " + error))
	}

	const getAdmissionInfo = () => {
		const body = {
			"patient_id": patientID
		}

		endpointProvider.post(LINKS.VISUALIZE.GET_ADMISSION_INFO, body).then(result => setAdmissionInfo(result))
	}

    useOnMount(() => {
		getModalitiesUnion(props.patientID).then(modalities => setAllPatientModalities(modalities)).catch(err => console.error(err))
		getModalitiesIntersection(props.patientID).catch(err => console.error(err))
		getStartEndTimestamps(props.patientID).catch(err => console.error(err))
		getUserInfo().catch(err => console.error(err))
		resetAnnotations()
		getLayouts()
		getEEGMontages()
		getEEGMontageMapping()
		getAdmissionInfo()
		getPersystRecordingInfo()
		getPersystMontages()
	})

	const validStartEndTimestampsResponse = (timestamps, file) => {
		return (file in timestamps) && ('monitoring_start_time' in timestamps[file]) && ('monitoring_end_time' in timestamps[file])
	}

	const isReadyToPrefetch =
		modalitiesLoaded &&
		patientModalitiesLoaded &&
		userInfoLoaded &&
		hotkeysLoaded &&
		dataObjectID &&
		patientID &&
		layoutsLoaded &&
		eegMontagesLoaded &&
		eegMontageMapping &&
		workspacesProvider.selectedWorkspace &&
		workspacesProvider.selectedWorkspace.length > 0 &&
		startEndTimestampsLoaded &&
		validStartEndTimestampsResponse(startEndTimestamps, dataObjectName) &&
		dataObjectName &&
		dataObjectName.length > 0 &&
		admissionInfo && 
		enoughSpace &&
		persystRecordingInfo !== undefined &&
		persystMontageDefinitions !== undefined

	// TODO: this staged loading needs a complete overhaul because it regularly causes many bugs
	useEffect(() => {
		if (isReadyToPrefetch && !prefetchIsFinished) {
			startPrefetch()
		}

		if (!ready && isReadyToPrefetch && prefetchIsFinished) {
			let { monitoring_start_time, monitoring_end_time, timezone } = startEndTimestamps[dataObjectName]

			if (!timezone) {
				timezone = "America/New_York"
			}

			setPatientFileInfo({
				fileStartDate: new Date(monitoring_start_time),
				fileEndDate: new Date(monitoring_end_time),
				patientId: patientID,
				dataSourceMap: new Map([
					[DataSource.CURRENT_PATIENT, dataObjectID],
					[DataSource.CPPOPT_ANALYSIS, 999999],
				]),
				patientModalities: Object.keys(patientModalities),
				montageMapping: eegMontageMapping,
				timeZone: timezone,
				isAdmitted: admissionInfo.is_admitted,
				persystRecordingInfo,
				persystMontageDefinitions
			})

			setReady(true)
		}
	})


	return ready
		?
		<VisualizationContext.Provider value={{patientID, dataObjectName, siteName, patient_id: patientIdentifier, modalities, patientModalities, hotkeys, setHotkeys, hotkeysLoaded, layoutsLoaded,
			resetHotkeyConfig, updateHotkeyConfig, startEndTimestamps: startEndTimestamps[dataObjectName], closeVisualization: handleCloseVisualization, 
			dataObjectID
		}}>
			<VisualizationArea />
		</VisualizationContext.Provider>
		:
		<div style={{ display: "flex", width: "100vw", height: "100vh", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#FFFFFF" }}>
			<div style={{ flex: 0, display: "flex", width: "100%", alignItems: "center", justifyContent: "right", padding: "20px" }}>
				<MobergButton onClick={goBack} shape={MobergButtonShape.SQUARE}>
					<MdIcons.MdOutlineClose size={MobergIconSize.LARGE} />
				</MobergButton>
			</div>

			<div style={{ flex: 1 }}>
				<div style={{ marginBottom: "100px" }}>
					<h1 style={{ marginBottom: "20px" }}>{dataObjectName}</h1>
					<h5>{siteName} </h5>
					<h5>Patient: {patientIdentifier} </h5>
				</div>

				<div>
					<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", background: "#FFFFFF" }}>
						<>
							<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#FFFFFF", height: "100%" }}>
								<img src={loadingSvg} alt="loading" style={{ width: "300px" }} />
								<h2 style={{ fontFamily: "Montserrat", fontStyle: "normal", fontWeight: "700", fontSize: "25px", marginTop: "10px" }}>Loading...</h2>
							</div>
							<div style={{ flex: 1 }}>
								<p>{modalitiesLoaded && patientModalitiesLoaded ? <Check /> : <Close />} Modalities</p>
								<p>{userInfoLoaded ? <Check /> : <Close />} User Info</p>
								<p>{hotkeysLoaded ? <Check /> : <Close />} Hotkeys</p>
								<p>{layoutsLoaded ? <Check /> : <Close />} Layouts</p>
								<p>{eegMontagesLoaded && eegMontageMapping ? <Check /> : <Close />} EEG Montages</p>
								<p>{startEndTimestampsLoaded ? <Check /> : <Close />} File Start and End Timestamps</p>
								<p>{workspacesProvider.selectedWorkspace ? <Check /> : <Close />} Workspace</p>
								<p>{dataObjectName ? <Check /> : <Close />} Patient File</p>
								<p>{persystRecordingInfo !== undefined && persystMontageDefinitions !== undefined ? <Check /> : <Close />} Persyst</p>
								{!prefetchDisabled && (<p>{prefetchIsFinished ? <Check /> : <Close />} {prefetchMessage} </p>)}
								<p style={{
									color: "#1890ff", cursor: "pointer", size: '22',
									visibility: (modalitiesLoaded && patientModalitiesLoaded && userInfoLoaded &&
										hotkeysLoaded && layoutsLoaded && eegMontagesLoaded && startEndTimestampsLoaded && workspacesProvider && ( isDevelopment || isDemo)  ) ? 'visible' : 'hidden'
								}}
									onClick={skipPrefetch}>Skip Prefetch Data</p>

							</div>
						</>
					</div>
				</div>
			</div>
		</div>
}

const Check = () => <MdIcons.MdCheck color={"green"} size={18} />
const Close = () => <MdIcons.MdClose color={"red"} size={18} />